import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Footer, Header } from "../../components";
import { campusPath, get_public_branches } from "../../apis/Branches";
import { C, FONT, container } from "../../constants/mq";

// Group an array of branch rows by their state name.
const groupByState = (rows) => {
  const map = new Map();
  (rows || []).forEach((r) => {
    const state = r.state || "Other";
    if (!map.has(state)) map.set(state, []);
    map.get(state).push(r);
  });
  return Array.from(map.entries()).map(([state, branches]) => ({ state, branches }));
};

// Red banner like CommonBanner, then white campus cards on sand — the same
// cards as the home page's "Tutoring locations" section.
const styles = {
  page: { background: C.sand, minHeight: "100vh" },
  hero: {
    background: `linear-gradient(112deg, ${C.bright} 0%, ${C.bright} 66%, #C2001A 66%, #C2001A 100%)`,
    color: "#ffffff",
    padding: "58px 0 54px",
    fontFamily: FONT,
  },
  heroInner: container,
  eyebrow: {
    display: "inline-block",
    fontSize: 12.5,
    fontWeight: 600,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.85)",
    margin: "0 0 14px",
  },
  heading: {
    fontSize: 40,
    fontWeight: 800,
    color: "#ffffff",
    margin: 0,
    lineHeight: 1.18,
    letterSpacing: "-0.5px",
    maxWidth: 680,
  },
  subheading: {
    fontSize: 15.5,
    lineHeight: 1.65,
    color: "rgba(255,255,255,0.92)",
    margin: "14px 0 0",
    maxWidth: 620,
  },

  body: { maxWidth: 1072, margin: "0 auto", padding: "56px 24px 96px", fontFamily: FONT },
  section: { marginBottom: 56 },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: C.ink,
    margin: 0,
    letterSpacing: "-0.3px",
  },
  sectionPill: (color) => ({
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    padding: "4px 10px",
    borderRadius: 4,
    color: color === "high" ? C.red : C.ink,
    background: "#ffffff",
    border: `1px solid ${C.line}`,
  }),
  stateGroup: { marginBottom: 28 },
  stateLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: C.muted,
    marginBottom: 12,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 24,
  },
  card: { padding: "26px 24px 28px", display: "flex", flexDirection: "column" },
  campusName: {
    fontSize: 19,
    fontWeight: 700,
    color: C.ink,
    margin: "0 0 4px",
  },
  campusSuburb: {
    fontSize: 13,
    color: C.muted,
    fontWeight: 600,
    margin: "0 0 12px",
  },
  // each card opens its campus page, which has the timetable
  timetableLink: { marginTop: "auto", paddingTop: 14, fontSize: 14, fontWeight: 600, alignSelf: "flex-start" },
  metaRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: 13.5,
    color: C.body,
    margin: "6px 0",
    lineHeight: 1.55,
  },
  metaIcon: { color: C.muted, flexShrink: 0, marginTop: 3 },
  emptyNote: {
    fontSize: 13,
    color: C.muted,
    fontStyle: "italic",
    padding: "10px 0",
  },
};

const Card = ({ b }) => (
  <div style={styles.card} className="mq-card mq-card--hover">
    <h4 style={styles.campusName}>
      <Link to={campusPath(b)} style={{ color: "inherit", textDecoration: "none" }}>
        {b.name}
      </Link>
    </h4>
    {(b.region || b.suburb) && <p style={styles.campusSuburb}>{b.region || b.suburb}</p>}
    {b.address && (
    <div style={styles.metaRow}>
      <svg style={styles.metaIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
      <a
        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(b.address || b.name || "")}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", textDecoration: "none" }}
        title="Open directions in Google Maps"
        className="address-link"
      >
        {b.address}
      </a>
    </div>
    )}
    {b.phone && (
      <div style={styles.metaRow}>
        <svg style={styles.metaIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <a href={`tel:${b.phone.replace(/\s+/g, "")}`} style={{ color: "inherit", textDecoration: "none" }}>
          {b.phone}
        </a>
      </div>
    )}
    {b.email && (
      <div style={styles.metaRow}>
        <svg style={styles.metaIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        <a href={`mailto:${b.email}`} style={{ color: "inherit", textDecoration: "none" }}>
          {b.email}
        </a>
      </div>
    )}
    <Link className="mq-link" style={styles.timetableLink} to={campusPath(b)}>
      View timetable
    </Link>
  </div>
);

const Section = ({ title, pillLabel, pillColor, groups, emptyMsg }) => (
  <div style={styles.section}>
    <div style={styles.sectionTitleRow}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <span style={styles.sectionPill(pillColor)}>{pillLabel}</span>
    </div>
    {groups.length === 0 ? (
      <div style={styles.emptyNote}>{emptyMsg}</div>
    ) : (
      groups.map((g) => (
        <div key={g.state} style={styles.stateGroup}>
          <div style={styles.stateLabel}>{g.state}</div>
          <div style={styles.grid}>
            {g.branches.map((b) => (
              <Card key={b.name + g.state} b={b} />
            ))}
          </div>
        </div>
      ))
    )}
  </div>
);

function OurBranches() {
  const [seniorGroups, setSeniorGroups] = useState([]);
  const [highGroups, setHighGroups] = useState([]);
  const [primaryGroups, setPrimaryGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [senior, high, primary] = await Promise.all([
        get_public_branches("senior"),
        get_public_branches("high"),
        get_public_branches("primary"),
      ]);
      if (cancelled) return;
      setSeniorGroups(groupByState(senior));
      setHighGroups(groupByState(high));
      setPrimaryGroups(groupByState(primary));
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={styles.page}>
      <Header />
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <span style={styles.eyebrow}>Our Branches</span>
          <h1 style={styles.heading}>Find a campus near you</h1>
          <p style={styles.subheading}>
            Year 11–12 and Year 7–10 programs run from our Sydney campuses.
            Primary programs are available across many more locations.
          </p>
        </div>
      </section>

      <div style={styles.body}>
        <Section
          title="High School"
          pillLabel="Year 11–12"
          pillColor="high"
          groups={seniorGroups}
          emptyMsg="No campuses listed yet."
        />
        <Section
          title="High School"
          pillLabel="Year 7–10"
          pillColor="high"
          groups={highGroups}
          emptyMsg="No campuses listed yet."
        />
        <Section
          title="Primary"
          pillLabel="Year 1–6"
          pillColor="primary"
          groups={primaryGroups}
          emptyMsg="No campuses listed yet."
        />
      </div>

      <Footer />

      <style>{`
        .address-link:hover {
          color: ${C.red} !important;
          text-decoration: underline !important;
        }
      `}</style>
    </div>
  );
}

export default OurBranches;
